import { Language } from "@/components/Inputs/LanguagePicker/Language.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import i18next from "i18next";
import { Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const LanguagePicker = () => {
  const rootAttributes = useEngineStore((state) => state.rootAttributes);
  const { shadowRoot } = useShadowRoot();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    shadowRoot.addEventListener("mousedown", handleClickOutside as EventListener);
    return () => {
      shadowRoot.removeEventListener("mousedown", handleClickOutside as EventListener);
    };
  }, [shadowRoot]);

  const handleLanguageChange = async (lang: string) => {
    await i18next.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
    const urlParams = new URLSearchParams(window.location.search);

    urlParams.set(URLParam.Language, lang);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${urlParams.toString()}`,
    );
    setIsOpen(false);
  };

  let languages = Object.values(Language);

  if (rootAttributes?.languages?.length) languages = rootAttributes.languages as Language[];

  const currentLanguage = i18next.language?.toUpperCase() || "EN";

  return (
    <div className="c-language-picker" ref={dropdownRef}>
      <button className="c-language-picker__trigger" onClick={() => setIsOpen(!isOpen)}>
        <Globe size={18} />
        <span className="c-language-picker__current">{currentLanguage}</span>
      </button>
      {isOpen && (
        <div className="c-language-picker__dropdown">
          {languages?.map((lang) => (
            <button
              key={lang}
              className={`c-language-picker__option ${
                lang.toUpperCase() === currentLanguage ? "c-language-picker__option--selected" : ""
              }`}
              onClick={() => handleLanguageChange(lang)}
            >
              {lang?.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
